// Admin compliance "Catalog" tab — minimal CRUD for both:
//   - cert_types               (Phase 5)
//   - child_health_record_types (Phase 6)
// Same shape, one shared sub-component. Intentionally small per scope:
//   code, label, description, default_validity_months, is_required,
//   blocks_attendance_when_missing, sort_order, active.
//   For child types: applies_age_min_months / applies_age_max_months too.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Trash, X, ToggleLeft, ToggleRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { listCertTypes, upsertCertType, deleteCertType } from "@/lib/compliance";
import { listChildHealthRecordTypes } from "@/lib/childHealth";
import { supabase } from "@/lib/supabase";

const SCOPES = [
  { key: "staff",    label: "Staff certifications", table: "cert_types",                  hasAge: false },
  { key: "children", label: "Child health records",  table: "child_health_record_types",   hasAge: true  },
];

export default function AdminComplianceCatalogTab() {
  const { profile } = useAuth();
  const orgId = profile?.organization_id;
  const [scope, setScope] = useState("staff");
  const [staffTypes, setStaffTypes] = useState([]);
  const [childTypes, setChildTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState({ open: false, type: null, scope: "staff" });

  const refresh = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const [s, c, locationResponse] = await Promise.all([
        listCertTypes(orgId, { includeInactive: true }),
        listChildHealthRecordTypes(orgId, { includeInactive: true }),
        supabase.from("locations").select("id, name").eq("organization_id", orgId).order("name", { ascending: true }),
      ]);
      if (locationResponse.error && locationResponse.error.code !== "42P01") throw locationResponse.error;
      setStaffTypes(s || []);
      setChildTypes(c || []);
      setLocations(locationResponse.data || []);
    } catch (err) {
      toast.error(err.message || "Could not load catalogs");
    } finally {
      setLoading(false);
    }
  }, [orgId]);
  useEffect(() => { refresh(); }, [refresh]);

  const types = scope === "staff" ? staffTypes : childTypes;
  const meta = SCOPES.find((s) => s.key === scope);

  const onAdd = () => setEditor({ open: true, type: null, scope });
  const onEdit = (t) => setEditor({ open: true, type: t, scope });

  const onToggleActive = async (t) => {
    try {
      if (scope === "staff") {
        await upsertCertType({
          id: t.id, organizationId: orgId, code: t.code, label: t.label,
          description: t.description, defaultValidityMonths: t.default_validity_months,
          isRequired: t.is_required, blocksAttendanceWhenMissing: t.blocks_attendance_when_missing,
          sortOrder: t.sort_order, active: !t.active,
        });
      } else {
        const { error } = await supabase
          .from("child_health_record_types")
          .update({ active: !t.active })
          .eq("id", t.id);
        if (error) throw error;
      }
      toast.success(!t.active ? "Activated" : "Deactivated");
      refresh();
    } catch (err) {
      toast.error(err.message || "Could not toggle");
    }
  };

  const onDelete = async (t) => {
    if (!window.confirm(`Delete "${t.label}"? This is only possible if no records reference it.`)) return;
    try {
      if (scope === "staff") {
        await deleteCertType(t.id);
      } else {
        const { error } = await supabase
          .from("child_health_record_types")
          .delete()
          .eq("id", t.id);
        if (error) {
          if (error.code === "23503") throw new Error("Type is in use — deactivate it instead.");
          throw error;
        }
      }
      toast.success("Deleted");
      refresh();
    } catch (err) {
      toast.error(err.message || "Could not delete");
    }
  };

  return (
    <div data-testid="admin-compliance-catalog" className="space-y-5">
      <nav role="tablist" aria-label="Catalog scope" className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white p-1 text-[12px] font-semibold">
        {SCOPES.map((s) => (
          <button
            key={s.key}
            role="tab"
            aria-selected={scope === s.key}
            data-testid={`admin-catalog-scope-${s.key}`}
            onClick={() => setScope(s.key)}
            className={`rounded-full px-3 py-1 transition ${scope === s.key ? "bg-primary text-white" : "text-neutral-700 hover:bg-neutral-50"}`}
          >
            {s.label}
          </button>
        ))}
      </nav>

      <section className="rounded-2xl border border-neutral-200 bg-white">
        <div className="flex items-center justify-between gap-3 px-4 py-3">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">Catalog</p>
            <p className="text-sm font-semibold text-neutral-900">{meta?.label}</p>
          </div>
          <button
            type="button"
            data-testid="admin-catalog-add"
            onClick={onAdd}
            className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-primary-hover"
          >
            <Plus size={11} weight="bold" /> Add type
          </button>
        </div>

        {loading && <p className="px-4 pb-4 text-sm text-neutral-500">Loading…</p>}

        {!loading && types.length === 0 && (
          <p data-testid="admin-catalog-empty" className="border-t border-neutral-100 px-4 py-6 text-center text-sm text-neutral-500">
            No types yet. Click <strong>Add type</strong> to create one.
          </p>
        )}

        {types.length > 0 && (
          <ul className="divide-y divide-neutral-100" data-testid="admin-catalog-list">
            {types.map((t) => (
              <li
                key={t.id}
                data-testid={`admin-catalog-row-${t.id}`}
                data-active={t.active ? "true" : "false"}
                className="flex flex-wrap items-center gap-3 px-4 py-3"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-neutral-900">
                    {t.label}{t.is_required ? " · required" : ""}
                    {t.blocks_attendance_when_missing ? " · attendance-blocking" : ""}
                  </p>
                  <p className="truncate text-[11px] text-neutral-500">
                    {t.code}
                    {t.default_validity_months ? ` · ${t.default_validity_months}mo validity` : " · no expiry"}
                    {meta?.hasAge && (t.applies_age_min_months || t.applies_age_max_months != null)
                      ? ` · ages ${t.applies_age_min_months || 0}${t.applies_age_max_months != null ? `–${t.applies_age_max_months}` : "+"} months`
                      : ""}
                    {meta?.hasAge ? healthScopeSummary(t, locations) : ""}
                    {!t.active ? " · inactive" : ""}
                  </p>
                </div>
                <button
                  type="button"
                  data-testid={`admin-catalog-toggle-${t.id}`}
                  onClick={() => onToggleActive(t)}
                  aria-pressed={t.active}
                  className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${t.active ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-neutral-300 bg-neutral-50 text-neutral-600"}`}
                >
                  {t.active ? <ToggleRight size={11} weight="duotone" /> : <ToggleLeft size={11} weight="duotone" />}
                  {t.active ? "Active" : "Inactive"}
                </button>
                <button
                  type="button"
                  data-testid={`admin-catalog-edit-${t.id}`}
                  onClick={() => onEdit(t)}
                  className="rounded-full border border-neutral-200 bg-white px-2.5 py-0.5 text-[11px] font-semibold text-neutral-700 hover:bg-neutral-50"
                >
                  Edit
                </button>
                <button
                  type="button"
                  data-testid={`admin-catalog-delete-${t.id}`}
                  onClick={() => onDelete(t)}
                  className="rounded-full border border-[#C6851E]/40 bg-white px-2.5 py-0.5 text-[11px] font-semibold text-[#8A5A12] hover:bg-[#C6851E]/5"
                >
                  <Trash size={11} weight="duotone" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <CatalogEditorSheet
        open={editor.open}
        onOpenChange={(o) => setEditor((s) => ({ ...s, open: o }))}
        type={editor.type}
        scope={editor.scope}
        organizationId={orgId}
        locations={locations}
        onSaved={refresh}
      />
    </div>
  );
}

function CatalogEditorSheet({ open, onOpenChange, type, scope, organizationId, locations, onSaved }) {
  const isEdit = !!type;
  const hasAge = scope === "children";
  const [code, setCode] = useState("");
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [validity, setValidity] = useState("");
  const [isRequired, setIsRequired] = useState(true);
  const [blocks, setBlocks] = useState(false);
  const [sortOrder, setSortOrder] = useState(100);
  const [active, setActive] = useState(true);
  const [ageMin, setAgeMin] = useState("");
  const [ageMax, setAgeMax] = useState("");
  const [locationIds, setLocationIds] = useState([]);
  const [programKeys, setProgramKeys] = useState("");
  const [jurisdictionLabel, setJurisdictionLabel] = useState("");
  const [sourceReference, setSourceReference] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCode(type?.code || "");
    setLabel(type?.label || "");
    setDescription(type?.description || "");
    setValidity(type?.default_validity_months ?? "");
    setIsRequired(type?.is_required ?? true);
    setBlocks(type?.blocks_attendance_when_missing ?? false);
    setSortOrder(type?.sort_order ?? 100);
    setActive(type?.active ?? true);
    setAgeMin(type?.applies_age_min_months ?? "");
    setAgeMax(type?.applies_age_max_months ?? "");
    setLocationIds(type?.location_ids || []);
    setProgramKeys((type?.program_keys || []).join(", "));
    setJurisdictionLabel(type?.jurisdiction_label || "");
    setSourceReference(type?.source_reference || "");
  }, [open, type]);

  const onSave = async () => {
    if (!code.trim()) { toast.error("Code is required"); return; }
    if (!label.trim()) { toast.error("Label is required"); return; }
    setBusy(true);
    try {
      if (scope === "staff") {
        await upsertCertType({
          id: type?.id,
          organizationId,
          code: code.trim().toLowerCase().replace(/\s+/g, "_"),
          label: label.trim(),
          description: description.trim() || null,
          defaultValidityMonths: validity === "" ? null : Number(validity),
          isRequired,
          blocksAttendanceWhenMissing: blocks,
          sortOrder: Number(sortOrder) || 100,
          active,
        });
      } else {
        const payload = {
          organization_id: organizationId,
          code: code.trim().toLowerCase().replace(/\s+/g, "_"),
          label: label.trim(),
          description: description.trim() || null,
          default_validity_months: validity === "" ? null : Number(validity),
          is_required: isRequired,
          blocks_attendance_when_missing: blocks,
          sort_order: Number(sortOrder) || 100,
          active,
          applies_age_min_months: ageMin === "" ? 0 : Number(ageMin),
          applies_age_max_months: ageMax === "" ? null : Number(ageMax),
          location_ids: locationIds,
          program_keys: programKeys.split(",").map((key) => key.trim()).filter(Boolean),
          jurisdiction_label: jurisdictionLabel.trim() || null,
          source_reference: sourceReference.trim() || null,
        };
        if (type?.id) {
          const { error } = await supabase
            .from("child_health_record_types").update(payload).eq("id", type.id);
          if (error) throw error;
        } else {
          const { error } = await supabase
            .from("child_health_record_types").insert(payload);
          if (error) throw error;
        }
      }
      toast.success(isEdit ? "Type updated" : "Type added");
      onSaved?.();
      onOpenChange?.(false);
    } catch (err) {
      toast.error(err.message || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="admin-catalog-editor"
        side="bottom"
        className="max-h-[92dvh] overflow-y-auto rounded-t-3xl sm:max-w-md sm:mx-auto"
      >
        <SheetHeader className="text-left">
          <SheetTitle className="font-heading text-xl">{isEdit ? "Edit type" : "Add type"}</SheetTitle>
          <SheetDescription>{scope === "staff" ? "Staff certification" : "Child health record"} catalog.</SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Code">
              <input data-testid="admin-catalog-editor-code" value={code} onChange={(e) => setCode(e.target.value)} className={INPUT} disabled={isEdit} />
            </Field>
            <Field label="Sort order">
              <input type="number" data-testid="admin-catalog-editor-sort" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} className={INPUT} />
            </Field>
          </div>
          <Field label="Label">
            <input data-testid="admin-catalog-editor-label" value={label} onChange={(e) => setLabel(e.target.value)} className={INPUT} />
          </Field>
          <Field label="Description (optional)">
            <textarea rows={2} data-testid="admin-catalog-editor-description" value={description} onChange={(e) => setDescription(e.target.value)} className={INPUT} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Default validity (months)">
              <input type="number" min="1" data-testid="admin-catalog-editor-validity" value={validity} onChange={(e) => setValidity(e.target.value)} className={INPUT} placeholder="e.g. 24" />
            </Field>
            <Field label="Active">
              <input type="checkbox" data-testid="admin-catalog-editor-active" checked={active} onChange={(e) => setActive(e.target.checked)} className="mt-2 h-4 w-4 rounded border-neutral-300 text-primary focus:ring-primary/30" />
            </Field>
          </div>
          {hasAge && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Age min (months)">
                  <input type="number" min="0" data-testid="admin-catalog-editor-age-min" value={ageMin} onChange={(e) => setAgeMin(e.target.value)} className={INPUT} placeholder="0" />
                </Field>
                <Field label="Age max (months, optional)">
                  <input type="number" min="0" data-testid="admin-catalog-editor-age-max" value={ageMax} onChange={(e) => setAgeMax(e.target.value)} className={INPUT} placeholder="(no cap)" />
                </Field>
              </div>
              <section className="rounded-2xl border border-neutral-200 bg-neutral-50/50 p-3">
                <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">Requirement scope <span className="font-normal normal-case text-neutral-400">(leave blank for every child who meets the age range)</span></p>
                <div className="mt-3 space-y-3">
                  {locations.length > 0 && (
                    <fieldset>
                      <legend className="text-xs font-medium text-neutral-700">Locations</legend>
                      <div className="mt-1.5 flex flex-wrap gap-2">
                        {locations.map((location) => {
                          const checked = locationIds.includes(location.id);
                          return <label key={location.id} className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${checked ? "border-primary/40 bg-primary/10 text-primary-hover" : "border-neutral-200 bg-white text-neutral-700"}`}>
                            <input type="checkbox" className="sr-only" checked={checked} onChange={() => setLocationIds((ids) => checked ? ids.filter((id) => id !== location.id) : [...ids, location.id])} />
                            {location.name}
                          </label>;
                        })}
                      </div>
                    </fieldset>
                  )}
                  <Field label="Classroom program keys">
                    <input data-testid="admin-catalog-editor-program-keys" value={programKeys} onChange={(e) => setProgramKeys(e.target.value)} className={INPUT} placeholder="e.g. toddler, preschool" />
                  </Field>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Field label="Jurisdiction or policy label">
                      <input data-testid="admin-catalog-editor-jurisdiction" value={jurisdictionLabel} onChange={(e) => setJurisdictionLabel(e.target.value)} className={INPUT} placeholder="Optional center context" />
                    </Field>
                    <Field label="Source reference">
                      <input data-testid="admin-catalog-editor-source" value={sourceReference} onChange={(e) => setSourceReference(e.target.value)} className={INPUT} placeholder="Optional policy link or note" />
                    </Field>
                  </div>
                </div>
              </section>
            </>
          )}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="flex items-start gap-2 rounded-xl border border-neutral-200 bg-neutral-50/40 p-3 text-[12px]">
              <input type="checkbox" data-testid="admin-catalog-editor-required" checked={isRequired} onChange={(e) => setIsRequired(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-primary focus:ring-primary/30" />
              <span><strong className="block">Required</strong>Counts toward "missing" rollups.</span>
            </label>
            <label className="flex items-start gap-2 rounded-xl border border-neutral-200 bg-neutral-50/40 p-3 text-[12px]">
              <input type="checkbox" data-testid="admin-catalog-editor-blocks" checked={blocks} onChange={(e) => setBlocks(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-primary focus:ring-primary/30" />
              <span><strong className="block">Attendance-blocking</strong>Drives the "Action needed" chip when missing/expired.</span>
            </label>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
          <button type="button" data-testid="admin-catalog-editor-cancel" onClick={() => onOpenChange?.(false)} className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50">
            <X size={12} weight="bold" /> Cancel
          </button>
          <button type="button" data-testid="admin-catalog-editor-save" disabled={busy} onClick={onSave} className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover disabled:opacity-60">
            {busy ? "Saving…" : isEdit ? "Save" : "Add type"}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

const INPUT = "w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-60";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">{label}</span>
      {children}
    </label>
  );
}

function healthScopeSummary(type, locations) {
  const parts = [];
  if ((type.location_ids || []).length > 0) {
    const names = (type.location_ids || [])
      .map((id) => locations.find((location) => location.id === id)?.name || "configured location");
    parts.push(` · ${names.join(", ")}`);
  }
  if ((type.program_keys || []).length > 0) parts.push(` · programs ${type.program_keys.join(", ")}`);
  if (type.jurisdiction_label) parts.push(` · ${type.jurisdiction_label}`);
  return parts.join("");
}
